import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CancelReason,
  type OrganizationBilling,
  type Plan,
  PlanKind,
  PlanStatus,
} from '../../entity/api/organization-billing';
import {
  abortDowngrade,
  cancelSubscription,
  checkProviderSession,
  getOrgBillingStatus,
  getPlansData,
  requestBillingPortalUrl,
  subscribeOrganization,
  syncBillingByOrgIdWithPaymentProvider,
  unCancelSubscription,
  updateSubscription,
} from '../../sdk/organization-billing';
import { endSubscriptionTrial } from '../../sdk/subscription';

// ============ QUERY KEYS ============

export const organizationBillingKeys = {
  all: ['organization-billing'] as const,
  byOrgId: function (orgId: string) {
    return [...organizationBillingKeys.all, orgId] as const;
  },
};

export const subscriptionPlansKeys = {
  all: ['subscription-plans'] as const,
  byOrgId: function (orgId?: string) {
    return [...subscriptionPlansKeys.all, orgId ?? 'default'] as const;
  },
};

// ============ QUERIES ============

/**
 * Fetch organization billing status by organization ID
 * Replaces: getStores().organizationBilling((s) => s.getByOrgIdFromStore(orgId))
 * Disabled when orgId is missing or empty to avoid GET /organization//billing-status (404).
 */
export function useOrganizationBilling(orgId: string | undefined) {
  const enabled = Boolean(orgId);
  return useQuery({
    queryKey: organizationBillingKeys.byOrgId(orgId ?? ''),
    queryFn: async function () {
      if (orgId == null) {
        throw new Error('Organization ID is required');
      }
      const response = await getOrgBillingStatus(orgId);
      return response.data;
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes - billing doesn't change often
  });
}

/**
 * Fetch subscription plans by organization ID
 * Returns plans sorted by monthlyPrice (ascending)
 * Replaces: getPlansData(organizationId) direct calls
 */
export function useSubscriptionPlans(orgId?: string) {
  return useQuery<Plan[]>({
    queryKey: subscriptionPlansKeys.byOrgId(orgId),
    queryFn: async function () {
      const response = await getPlansData(orgId);
      // Sort by monthlyPrice (ascending) - consistent with component usage
      // REMOVE legacy plans and lite ones for now
      return response.data.plans
        .filter(
          (plan: Plan) =>
            plan.kind === PlanKind.Grow ||
            plan.kind === PlanKind.Scale ||
            plan.kind === PlanKind.Enterprise
        )
        .sort((a: Plan, b: Plan) => a.pricePerMonth - b.pricePerMonth);
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - plans don't change often
  });
}

// ============ MUTATIONS ============

/**
 * Sync billing with payment provider (Stripe)
 * Replaces: getStores().organizationBilling.getState().syncBillingByOrgIdWithPaymentProvider(orgId)
 */
export function useSyncBillingWithProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (orgId: string) {
      const response = await syncBillingByOrgIdWithPaymentProvider(orgId);
      return response.data;
    },
    onSuccess: function (data, orgId) {
      // Update the cache with the synced data
      queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId), data);
    },
  });
}

/**
 * Check provider session (for payment verification)
 * Replaces: getStores().organizationBilling.getState().checkProviderSession(orgId, sessionId)
 */
export function useCheckProviderSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ orgId, sessionId }: { orgId: string; sessionId: string }) {
      const response = await checkProviderSession(orgId, sessionId);
      return response;
    },
    onSuccess: function (_, { orgId }) {
      // Invalidate to refetch updated billing status
      void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
    },
  });
}

/**
 * Subscribe organization to a plan
 * Replaces: getStores().organizationBilling.getState().subscribeByOrgId(orgId, planId, toltReferral)
 */
export function useSubscribeOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      orgId,
      planId,
      toltReferral,
    }: {
      orgId: string;
      planId: string;
      toltReferral: string | null;
    }) {
      const response = await subscribeOrganization(orgId, planId, toltReferral);
      return response;
    },
    onSuccess: function (_, { orgId }) {
      // Invalidate to refetch updated billing status
      void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
    },
  });
}

/**
 * Request billing portal URL (Stripe customer portal)
 * Replaces: getStores().organizationBilling.getState().requestBillingPortalUrlByOrgId(orgId)
 */
export function useRequestBillingPortalUrl() {
  return useMutation({
    mutationFn: async function (orgId: string) {
      const response = await requestBillingPortalUrl(orgId);
      return response;
    },
    // No cache invalidation needed - just returns a URL
  });
}

/**
 * Cancel subscription
 * Replaces: getStores().organizationBilling.getState().cancelSubscriptionByOrgId(orgId)
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      orgId,
      feedback,
      cancelDetails,
    }: {
      orgId: string;
      feedback: CancelReason;
      cancelDetails: string;
    }) {
      const response = await cancelSubscription(orgId, {
        reason: feedback,
        extraDetails: cancelDetails,
      });
      return { response, orgId };
    },
    onMutate: async function (orgId) {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: organizationBillingKeys.byOrgId(orgId.orgId) });

      // Snapshot previous value
      const previousBilling = queryClient.getQueryData<OrganizationBilling>(
        organizationBillingKeys.byOrgId(orgId.orgId)
      );

      // Optimistically update: trialing cancels immediately so go straight to Cancelled,
      // otherwise set to Cancelling (active until end of billing period).
      if (previousBilling != null) {
        queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId.orgId), {
          ...previousBilling,
          currentPlanStatus:
            previousBilling.currentPlanStatus === PlanStatus.Trialing
              ? PlanStatus.Cancelled
              : PlanStatus.Cancelling,
        });
      }

      return { previousBilling };
    },
    onError: function (_err, orgId, context) {
      // Rollback on error
      if (context?.previousBilling != null) {
        queryClient.setQueryData(
          organizationBillingKeys.byOrgId(orgId.orgId),
          context.previousBilling
        );
      }
    },
    onSettled: function (_, __, ___) {
      // NOTE: we are not invalidating the query here because we want to keep the "Cancelling" status until the subscription is actually cancelled.
    },
  });
}

/**
 * Un-cancel subscription (reactivate)
 * Replaces: getStores().organizationBilling.getState().unCancelSubscriptionByOrgId(orgId)
 */
export function useUnCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (orgId: string) {
      const response = await unCancelSubscription(orgId);
      return { response, orgId };
    },
    onMutate: async function (orgId) {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });

      // Snapshot previous value
      const previousBilling = queryClient.getQueryData<OrganizationBilling>(
        organizationBillingKeys.byOrgId(orgId)
      );

      // Optimistically update to "Subscribed" status
      if (previousBilling != null) {
        queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId), {
          ...previousBilling,
          currentPlanStatus: PlanStatus.Subscribed,
        });
      }

      return { previousBilling };
    },
    onError: function (_err, orgId, context) {
      // Rollback on error
      if (context?.previousBilling != null) {
        queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId), context.previousBilling);
      }
      // Only invalidate on error so the rollback stays accurate
      void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
    },
  });
}

/**
 * Update subscription (change plan)
 * Replaces: getStores().organizationBilling.getState().updateSubscriptionByOrgId(orgId, planId, discountCoupon)
 */
export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      orgId,
      planId,
      discountCoupon,
    }: {
      orgId: string;
      planId: string;
      discountCoupon?: string | null;
    }) {
      const response = await updateSubscription(orgId, planId, discountCoupon);
      return response.data;
    },
    onSuccess: function (_, { orgId }) {
      // Invalidate to refetch updated billing status
      void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
    },
  });
}

/**
 * Abort a pending end-of-period downgrade.
 * Releases the Stripe schedule and clears pendingDowngrade from the billing record.
 */
export function useAbortDowngrade() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (orgId: string) {
      const response = await abortDowngrade(orgId);
      return response;
    },
    onSuccess: function (_, orgId) {
      void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
    },
  });
}

/**
 * End subscription trial for an organization
 */
export function useEndSubscriptionTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (orgId: string) {
      await endSubscriptionTrial(orgId);
    },
    onSuccess: function (_, orgId) {
      void queryClient.invalidateQueries({
        queryKey: organizationBillingKeys.byOrgId(orgId),
      });
    },
  });
}

// ============ CACHE HELPERS ============

/**
 * Get organization billing from cache (imperative access)
 */
export function getOrganizationBillingFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): OrganizationBilling | undefined {
  return queryClient.getQueryData<OrganizationBilling>(organizationBillingKeys.byOrgId(orgId));
}

/**
 * Invalidate organization billing to force refetch
 */
export function invalidateOrganizationBilling(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): void {
  void queryClient.invalidateQueries({ queryKey: organizationBillingKeys.byOrgId(orgId) });
}

/**
 * Update the hasFreeArticleCreated flag in cache
 * Used when an article is created to mark that the free article has been used
 */
export function markFreeArticleCreated(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): void {
  const currentBilling = queryClient.getQueryData<OrganizationBilling>(
    organizationBillingKeys.byOrgId(orgId)
  );

  if (currentBilling != null) {
    queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId), {
      ...currentBilling,
      hasFreeArticleCreated: true,
    });
  }
}

// ============ IMPERATIVE FUNCTIONS (for use outside React components) ============

/**
 * Sync billing with payment provider imperatively
 * Use in router-auth.tsx or other non-React contexts
 */
export async function syncBillingImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): Promise<OrganizationBilling> {
  const response = await syncBillingByOrgIdWithPaymentProvider(orgId);
  // Update the TanStack Query cache with the synced data
  queryClient.setQueryData(organizationBillingKeys.byOrgId(orgId), response.data);

  return response.data;
}

/**
 * Fetch organization billing imperatively and cache it
 * Use in router-auth.tsx or other non-React contexts
 */
export async function fetchOrganizationBillingImperative(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
): Promise<OrganizationBilling> {
  const billing = await queryClient.fetchQuery({
    queryKey: organizationBillingKeys.byOrgId(orgId),
    queryFn: async function () {
      const response = await getOrgBillingStatus(orgId);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  return billing;
}
